import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowUpRight, Check, ShieldAlert } from "lucide-react";
import { getService, PROGRAMS, DISCLAIMER, SERVICES } from "@/data/services";
import { getArticle } from "@/data/articles";
import { FAQS } from "@/data/faqs";
import { Seo, breadcrumbSchema, orgSchema } from "@/components/layout/Seo";
import { PageHero, Reveal, SectionHead } from "@/components/ui/motion";
import { FaqList } from "@/components/sections/FaqSection";
import { ConsultForm } from "@/components/sections/ConsultForm";
import { Timeline } from "@/components/sections/Timeline";
import { waLink } from "@/lib/config";

const TYPE_MAP = { pinjol: "Pinjaman Online", "kartu-kredit": "Kartu Kredit", kta: "KTA" };

export default function ServicePage() {
  const { slug } = useParams();
  const s = getService(slug);
  if (!s) return <Navigate to="/" replace />;
  const crumbs = [{ to: "/", label: "Beranda" }, { to: `/layanan/${s.slug}`, label: s.name }];
  const serviceSchema = { "@context": "https://schema.org", "@type": "Service", name: s.name, serviceType: "Konsultasi dan pendampingan penyelesaian kewajiban finansial", provider: orgSchema, areaServed: "ID", description: s.seoDescription };
  const related = s.articles.map(getArticle).filter(Boolean);

  return (
    <>
      <Seo title={s.seoTitle} description={s.seoDescription} path={`/layanan/${s.slug}`} schema={[serviceSchema, breadcrumbSchema(crumbs)]} />
      <PageHero crumbs={crumbs} eyebrow={`Layanan ${s.index} — ${s.short}`} lines={[s.headline]} desc={s.description}>
        <Reveal delay={0.4} className="mt-10 flex flex-wrap gap-3">
          <a href={waLink(s.key)} target="_blank" rel="noopener noreferrer" className="btn btn-sky !px-7 !py-4" data-testid="service-hero-wa">{s.cta} <ArrowUpRight size={16} /></a>
          <Link to={`/layanan/${s.slug}#konsultasi`} className="btn btn-ghost !px-7 !py-4" data-testid="service-hero-form">Isi Formulir</Link>
        </Reveal>
      </PageHero>

      <section className="py-20 sm:py-28" data-testid="service-detail">
        <div className="wrap grid grid-cols-1 gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden rounded-[1.5rem] rounded-tr-[9rem]">
              <img src={s.image} alt={s.imageAlt} className="h-full w-full object-cover" />
            </div>
          </Reveal>
          <div className="lg:col-span-6 lg:col-start-7">
            <SectionHead eyebrow="Ruang Lingkup" title="Apa yang kami bantu." />
            <ul className="mt-10 space-y-4">
              {s.points.map((p, i) => (
                <Reveal key={p} delay={i * 0.05}>
                  <li className="flex gap-4 rounded-2xl border border-line p-5 text-[16px] leading-relaxed text-ink"><Check className="mt-0.5 shrink-0 text-accentb" size={20} strokeWidth={2.5} />{p}</li>
                </Reveal>
              ))}
            </ul>
            <Reveal><p className="mt-12 text-[12px] font-bold uppercase tracking-[0.2em] text-ink-soft">Situasi yang sering kami temui</p></Reveal>
            <ul className="mt-4 border-t border-line">
              {s.situations.map((x, i) => (
                <li key={x} className="flex gap-5 border-b border-line py-5 font-display text-lg font-semibold text-slateb"><span className="text-sm text-accentb">0{i + 1}</span>{x}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-white pb-20 sm:pb-28">
        <div className="wrap">
          <SectionHead eyebrow="Pilihan Penyelesaian" title="Opsi yang mungkin dibicarakan." />
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {PROGRAMS.map((p, i) => (
              <Reveal key={p.no} delay={i * 0.06}>
                <article className="card h-full p-8" data-testid={`service-program-${p.key}`}><span className="text-[12px] font-bold uppercase tracking-[0.2em] text-accentb">Program {p.no}</span><span className="mt-4 block font-display text-5xl font-extrabold tracking-[-0.04em] text-sky-brand">{p.statPrefix && <span className="serif-it mr-1 text-2xl font-normal text-accentb">{p.statPrefix}</span>}{p.stat}{p.statSuffix}</span><span className="mt-1 block text-[12.5px] font-semibold text-ink-soft">{p.statLabel}</span><h3 className="mt-6 font-display text-xl font-bold">{p.title}</h3><p className="mt-3 text-[15px] leading-relaxed text-ink">{p.text}</p></article>
              </Reveal>
            ))}
          </div>
          <p className="mt-8 flex gap-3 rounded-2xl bg-ice p-5 text-[14px] leading-relaxed text-ink" data-testid="service-disclaimer"><ShieldAlert size={18} className="mt-0.5 shrink-0 text-accentb" />{DISCLAIMER}</p>
        </div>
      </section>

      <Timeline />

      <section className="py-20 sm:py-28">
        <div className="wrap grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <SectionHead eyebrow="Bacaan Terkait" title="Pahami dulu, putuskan kemudian." />
            <div className="mt-8 space-y-3">
              {SERVICES.filter((x) => x.slug !== s.slug).map((x) => (
                <Link key={x.slug} to={`/layanan/${x.slug}`} className="link-u block text-sm font-semibold text-slateb" data-testid={`other-service-${x.key}`}>Layanan {x.name} →</Link>
              ))}
            </div>
          </div>
          <div className="grid gap-4 lg:col-span-8">
            {related.map((a) => (
              <Link key={a.slug} to={`/artikel/${a.slug}`} className="card group flex items-center justify-between gap-6 p-6 transition-colors hover:border-sky-brand" data-testid={`related-article-${a.slug}`}>
                <span><span className="text-[12px] font-bold uppercase tracking-[0.16em] text-accentb">{a.category}</span><span className="mt-2 block font-display text-lg font-bold text-slateb sm:text-xl">{a.title}</span></span>
                <ArrowUpRight className="shrink-0 text-accentb transition-transform duration-300 group-hover:rotate-45" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white pb-20 sm:pb-28">
        <div className="wrap grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4"><SectionHead eyebrow="FAQ" title="Pertanyaan umum." /></div>
          <div className="lg:col-span-8"><FaqList items={[FAQS[3], FAQS[4], FAQS[5], FAQS[7], FAQS[9]]} /></div>
        </div>
      </section>

      <ConsultForm defaultType={TYPE_MAP[s.key]} />
    </>
  );
}
